import { useState } from 'react';
import { useNavigate } from 'react-router';
import { LayoutTemplate, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { type RuleTemplate, type RulesCatalog } from '@/types/rules.types';
import { useCreateTicketRule } from '../hooks/use-ticket-rules';
import { summarizeRule } from '../lib/rule-summary';
import { RULES_PATH } from './managed-by-rules-notice';
import { placeOf, type PlaybookPlace } from '../lib/places';

const ALL = 'All';

interface TemplateGalleryProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	catalog: RulesCatalog;
	/** The settings page it opens from: only templates that belong there are offered. */
	place: PlaybookPlace;
}

/**
 * Ready-made playbooks. "Use this" makes a copy for the brand, as a Draft, and opens it in the
 * builder to test before switching it on.
 */
export function TemplateGallery({ open, onOpenChange, catalog, place }: TemplateGalleryProps) {
	const navigate = useNavigate();
	const create = useCreateTicketRule();
	const [category, setCategory] = useState(ALL);
	const offered = catalog.templates.filter((template) => placeOf(template.definition) === place);
	const categories = [ALL, ...new Set(offered.map((template) => template.category))];
	const shown = offered.filter((template) => category === ALL || template.category === category);
	const pendingId = create.isPending && create.variables && 'templateId' in create.variables ? create.variables.templateId : null;

	const use = async (template: RuleTemplate) => {
		try {
			const created = await create.mutateAsync({ templateId: template.id });
			onOpenChange(false);
			navigate(`${RULES_PATH}/${created.id}`);
		} catch (error) {
			console.error('[TemplateGallery.use] failed to create from the template', { templateId: template.id, error });
			toast.error(getApiErrorMessage(error, 'Could not use this template. Please try again.'));
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className='flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-[1040px] flex-col gap-0 overflow-hidden rounded-[20px] border border-gray-500 p-0 sm:max-w-[1040px]'>
				<div className='flex items-start justify-between gap-4 px-6 pt-5 pb-4'>
					<div className='flex items-start gap-3'>
						<span className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-ink'>
							<LayoutTemplate className='size-4.5' aria-hidden />
						</span>
						<div>
							<DialogTitle className='text-[16px] font-semibold leading-6 text-ink'>Start from a template</DialogTitle>
							<DialogDescription className='mt-0.5 text-[13px] leading-[18px] text-gray-900'>
								Pick one that fits. It opens as a Draft, so you can test it before it touches a real message.
							</DialogDescription>
						</div>
					</div>
					<button
						type='button'
						aria-label='Close'
						onClick={() => onOpenChange(false)}
						className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-900 hover:bg-gray-100'
					>
						<X className='size-4' />
					</button>
				</div>

				{categories.length > 2 && (
					<div role='tablist' aria-label='Template categories' className='flex flex-wrap gap-2 px-6 pb-4'>
						{categories.map((name) => (
							<button
								key={name}
								type='button'
								role='tab'
								aria-selected={category === name}
								onClick={() => setCategory(name)}
								className={cn(
									'h-7 cursor-pointer rounded-full border px-3 text-[13px] transition-colors',
									category === name ? 'border-ink bg-ink text-white' : 'border-gray-500 bg-white text-ink hover:bg-gray-100'
								)}
							>
								{name}
							</button>
						))}
					</div>
				)}

				<div className='min-h-0 flex-1 overflow-y-auto border-t border-gray-300 bg-gray-50 px-6 py-5'>
					{shown.length === 0 ? (
						<p className='py-12 text-center text-[13px] text-gray-800'>No templates yet.</p>
					) : (
						<ul className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
							{shown.map((template) => (
								<li
									key={template.id}
									className='flex flex-col gap-3 rounded-2xl border border-gray-500 bg-white p-4 transition-[box-shadow,translate] hover:-translate-y-0.5 hover:shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] motion-reduce:hover:translate-y-0'
								>
									<div className='flex items-start justify-between gap-2'>
										<h3 className='text-[14px] font-semibold leading-5 text-ink'>{template.name}</h3>
										<span className='shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-900'>{template.category}</span>
									</div>
									<p className='text-[13px] leading-[18px] text-gray-900'>{template.description}</p>
									<p className='line-clamp-3 rounded-xl bg-gray-50 px-3 py-2 text-[12px] leading-4 text-gray-900'>
										{summarizeRule(template.definition, catalog)}
									</p>
									<PillButton
										variant='primary'
										className='mt-auto w-fit'
										onClick={() => void use(template)}
										disabled={create.isPending}
										aria-label={`Use ${template.name}`}
									>
										{pendingId === template.id ? 'Creating…' : 'Use this'}
									</PillButton>
								</li>
							))}
						</ul>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
